// palpiti-configurator.js — vanilla custom element, zero @theme deps.
// v8: each charm is a PRODUCT with real Shopify variants (metallo/pietra/smalto);
// the tile carries a variant <select>, state stores [charmId, variantIndex] pairs.
// Pure logic (totalCents/assignSlots/stateToQuery/queryToState) mirrors
// docs/superpowers/verify/verify_config.py — change one, change both, re-run harness.
class PalpitiConfigurator extends HTMLElement {
  connectedCallback() {
    const id = this.dataset.configId;
    this.cfg = JSON.parse(this.querySelector(`#${id}`).textContent);
    this.maxCharms = parseInt(this.dataset.maxCharms, 10) || 8;
    this.moneyFormat = this.dataset.moneyFormat || '€{{amount}}';
    this.stage = this.querySelector('[data-pc-stage]');
    this.baseImg = this.stage.querySelector('[data-layer="base"]');
    this.rail = this.querySelector('[data-pc-rail]');
    this.priceEl = this.querySelector('[data-pc-price]');
    this.countEl = this.querySelector('[data-pc-count]');
    this.selectedEl = this.querySelector('[data-pc-selected]');
    this.cache = new Map(); // src -> Promise (decoded)

    const firstBase = Object.keys(this.cfg.bases)[0];
    this.state = { base: firstBase, baseVariant: 0, charms: [] }; // charms: [[charmId, variantIndex], ...]
    this.prevTotal = null;

    this.buildControls();
    this.injectChrome();
    this.wireEvents();
    this.wireTabs();
    this.hydrateFromUrl();
    this.render();
    this.preloadEager();
    this.renderExternalExamples();
  }

  // URL del preset (stessi parametri di stateToQuery) per la pagina prodotto.
  presetToQuery(preset) {
    if (!preset) return '';
    const parts = [`base=${encodeURIComponent(preset.base || '')}`];
    (preset.items || []).slice(0, this.maxCharms).forEach(([cid, vi], i) =>
      parts.push(`charm${i + 1}=${encodeURIComponent(cid)}` + (vi ? `.${vi}` : '')));
    return parts.join('&');
  }

  // Popola la sezione "Esempi finiti" (sections/palpiti-examples.liquid): foto + nome + prezzo.
  // Selezionabili: il click porta alla pagina prodotto con la configurazione caricata (URL preset).
  renderExternalExamples() {
    const host = document.querySelector('[data-pc-examples]');
    if (!host) return;
    const ex = this.cfg.examples || [];
    const path = location.pathname;
    host.innerHTML = ex.map((e) => {
      // esempio-prodotto: link diretto alla sua pagina; esempio-preset: pagina configuratore col preset
      const q = this.presetToQuery(e.preset);
      const href = e.url ? e.url : `${path}${q ? '?' + q : ''}`;
      const price = (e.priceFrom ? 'da ' : '') + this.formatMoney(e.priceCents);
      return `<a class="pc-example pc-example--link" href="${this.esc(href)}">`
        + `<img src="${this.tileSrc(e)}" alt="${this.esc(e.label)}" loading="lazy" width="160" height="160">`
        + `<span class="pc-example-name">${this.esc(e.label)}</span>`
        + `<span class="pc-example-price">${price}</span></a>`;
    }).join('');
  }

  variant(cid, vi) { const c = this.cfg.charms[cid]; if (!c || !c.variants) return null; return c.variants[vi] || c.variants[0]; }

  // Safe image source: thumb, else cutout, else '' (never throws on missing img — e.g. metafield examples without thumb/img)
  tileSrc(o) { return (o && (o.thumb || (o.img && o.img.src))) || ''; }

  // Variant-aware images: la foto della variante se presente, altrimenti quella del charm.
  pieceThumb(c, v) { return (v && (v.thumb || (v.img && v.img.src))) || this.tileSrc(c); }
  pieceImg(c, v) { return (v && v.img) || (c && c.img) || { src: '', src2x: '' }; }

  // Base con varianti di finitura (solo Catenella): l'unità selezionata, o la base stessa se senza varianti.
  baseUnit(base) {
    if (base && base.variants && base.variants.length) return base.variants[this.state.baseVariant] || base.variants[0];
    return base;
  }

  // Build tabs + base/charm/example tiles CLIENT-SIDE from the island config,
  // so the configurator is fully driven by the JSON island (no metafield needed).
  buildControls() {
    const panel = this.querySelector('[data-pc-controls]');
    if (!panel) return; // controls already server-rendered (e.g. metafield present)
    const i = this.cfg.i18n || {};
    const charms = Object.entries(this.cfg.charms);
    const hasEx = (this.cfg.examples || []).length > 0;
    const tab = (name, label, on) =>
      `<button type="button" class="pc-tab" role="tab" data-tab="${name}" aria-selected="${on ? 'true' : 'false'}">${this.esc(label)}</button>`;
    const tabs = `<div class="pc-tabs" role="tablist">`
      + tab('base', i.baseLabel || 'Base', true)
      + tab('elemento', i.elementiLabel || 'Elementi', false)
      + tab('ciondolo', i.ciondoliLabel || 'Ciondoli', false)
      + `</div>`;
    const baseTiles = Object.entries(this.cfg.bases).map(([bid, b], idx) => {
      const bv0 = b.variants && b.variants[0];
      const sel = (b.variants && b.variants.length > 1)
        ? `<select class="pc-base-variant-select pc-variant-select" data-base-id="${bid}" aria-label="Finitura ${this.esc(b.label)}">`
          + b.variants.map((v, vi) => `<option value="${vi}">${this.esc(v.label)} · ${this.formatMoney(v.priceCents)}</option>`).join('')
          + `</select>` : '';
      return `<div class="pc-base">`
        + `<label class="pc-base-tile"><input type="radio" name="pc-base" value="${bid}" ${idx === 0 ? 'checked' : ''}>`
        + `<img src="${this.pieceThumb(b, bv0)}" alt="${this.esc(b.label)}" loading="lazy" width="100" height="100">`
        + `<span class="pc-base-name">${this.esc(b.label)}</span>`
        + `<span class="pc-base-price">${this.formatMoney(bv0 ? bv0.priceCents : b.priceCents)}</span></label>${sel}</div>`;
    }).join('');
    const basePanel = `<div class="pc-tabpanel" data-panel="base" role="tabpanel">`
      + `<p class="pc-hint">Scegli il bracciale di partenza.</p><div class="pc-base-grid">${baseTiles}</div></div>`;
    const charmPanel = (cat, hint) => {
      const tiles = charms.filter(([, c]) => c.category === cat && c.variants && c.variants.length).map(([cid, c]) => {
        const v0 = c.variants[0];
        const sel = c.variants.length > 1
          ? `<select class="pc-variant-select" data-charm-id="${cid}" aria-label="Finitura ${this.esc(c.label)}">`
            + c.variants.map((v, vi) => `<option value="${vi}" data-cents="${v.priceCents}">${this.esc(v.label)} · ${this.formatMoney(v.priceCents)}</option>`).join('')
            + `</select>` : '';
        return `<div class="pc-charm"><button type="button" class="pc-charm-tile" data-charm-id="${cid}" aria-pressed="false">`
          + `<img src="${this.pieceThumb(c, v0)}" alt="${this.esc(c.label)}" loading="lazy" width="100" height="100">`
          + `<span class="pc-charm-name">${this.esc(c.label)}</span>`
          + `<span class="pc-charm-price" data-pc-tile-price>${this.formatMoney(v0.priceCents)}</span></button>${sel}</div>`;
      }).join('');
      return `<div class="pc-tabpanel" data-panel="${cat}" role="tabpanel" hidden><p class="pc-hint">${this.esc(hint)}</p><div class="pc-charm-grid">${tiles}</div></div>`;
    };
    panel.innerHTML = tabs + basePanel + charmPanel('elemento', 'Aggiungi elementi e scegli la finitura.')
      + charmPanel('ciondolo', 'Aggiungi i ciondoli e scegli la finitura.');
  }

  injectChrome() {
    this.badges = {};
    this.querySelectorAll('.pc-tab').forEach((tab) => {
      const cat = tab.dataset.tab;
      if (cat === 'elemento' || cat === 'ciondolo') {
        const b = document.createElement('span');
        b.className = 'pc-tab-badge'; b.hidden = true;
        tab.appendChild(b); this.badges[cat] = b;
      }
    });
    this.maxNotes = [];
    this.querySelectorAll('.pc-tabpanel[data-panel="elemento"], .pc-tabpanel[data-panel="ciondolo"]').forEach((p) => {
      const n = document.createElement('p');
      n.className = 'pc-max-note'; n.hidden = true;
      n.textContent = `Hai raggiunto il massimo di ${this.maxCharms} pezzi. Rimuovi un pezzo per aggiungerne altri.`;
      p.insertBefore(n, p.firstChild); this.maxNotes.push(n);
    });
    const bar = this.querySelector('.pc-bar');
    const addBtn = this.querySelector('[data-pc-add]');
    if (bar && addBtn) {
      this.breakdownBtn = document.createElement('button');
      this.breakdownBtn.type = 'button';
      this.breakdownBtn.className = 'pc-breakdown-toggle';
      this.breakdownBtn.setAttribute('aria-expanded', 'false');
      this.breakdownBtn.addEventListener('click', () => this.toggleBreakdown());
      this.priceEl.parentElement.appendChild(this.breakdownBtn);

      this.breakdownEl = document.createElement('div');
      this.breakdownEl.className = 'pc-breakdown'; this.breakdownEl.hidden = true;
      bar.parentNode.insertBefore(this.breakdownEl, bar);

      this.shareBtn = document.createElement('button');
      this.shareBtn.type = 'button';
      this.shareBtn.className = 'pc-share';
      this.shareBtn.textContent = 'Condividi';
      this.shareBtn.addEventListener('click', () => this.share());
      bar.insertBefore(this.shareBtn, addBtn);
    }
  }

  wireTabs() {
    this.tabs = [...this.querySelectorAll('.pc-tab')];
    this.panels = [...this.querySelectorAll('.pc-tabpanel')];
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('tabindex', tab.getAttribute('aria-selected') === 'true' ? '0' : '-1');
      tab.addEventListener('click', () => this.selectTab(tab.dataset.tab));
      tab.addEventListener('keydown', (e) => {
        const k = e.key;
        if (k !== 'ArrowRight' && k !== 'ArrowLeft' && k !== 'Home' && k !== 'End') return;
        e.preventDefault();
        const j = k === 'Home' ? 0 : k === 'End' ? this.tabs.length - 1
          : k === 'ArrowRight' ? (i + 1) % this.tabs.length : (i - 1 + this.tabs.length) % this.tabs.length;
        this.selectTab(this.tabs[j].dataset.tab);
        this.tabs[j].focus();
      });
    });
  }
  selectTab(name) {
    this.tabs.forEach((t) => {
      const on = t.dataset.tab === name;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
    });
    this.panels.forEach((p) => { p.hidden = p.dataset.panel !== name; });
  }

  // ---- pure logic (identical to verify_config.py) ----
  totalCents() {
    const base = this.cfg.bases[this.state.base];
    const bu = this.baseUnit(base);
    let t = bu ? bu.priceCents : 0;
    for (const [cid, vi] of this.state.charms) { const v = this.variant(cid, vi); if (v) t += v.priceCents; }
    return t;
  }
  assignSlots() {
    return this.state.charms.slice(0, this.maxCharms).map(([cid, vi], i) => [cid, vi, i + 1]);
  }
  stateToQuery() {
    const parts = [`base=${this.state.base}`];
    this.state.charms.slice(0, this.maxCharms).forEach(([cid, vi], i) =>
      parts.push(`charm${i + 1}=${cid}` + (vi ? `.${vi}` : '')));
    return parts.join('&');
  }
  queryToState(qs) {
    const p = new URLSearchParams(qs);
    let base = null; // last valid base wins (mirror verify_config.py)
    const pairs = [];
    for (const [k, v] of p) {
      if (k === 'base') { if (this.cfg.bases[v]) base = v; continue; }
      const m = /^charm(\d+)$/.exec(k);
      if (!m) continue;
      const dot = v.indexOf('.');
      const cid = dot === -1 ? v : v.slice(0, dot);
      const vc = this.cfg.charms[cid] && this.cfg.charms[cid].variants;
      if (!vc || !vc.length) continue;
      let vi = 0; // .isdigit() semantics: non-numeric suffix -> default variant
      if (dot !== -1) { const vs = v.slice(dot + 1); if (/^\d+$/.test(vs)) vi = parseInt(vs, 10); }
      if (!(vi >= 0 && vi < vc.length)) vi = 0;
      pairs.push([parseInt(m[1], 10), [cid, vi]]);
    }
    pairs.sort((a, b) => a[0] - b[0]);
    return { base, charms: pairs.map((x) => x[1]).slice(0, this.maxCharms) };
  }

  // ---- rendering ----
  async swapLayer(img, layer) {
    if (!layer || !layer.src) { img.removeAttribute('src'); img.removeAttribute('srcset'); img.hidden = true; return; }
    const { src, src2x } = layer;
    try {
      if (!this.cache.has(src)) {
        const p = new Promise((res, rej) => {
          const im = new Image();
          im.onload = () => im.decode().then(() => res(im)).catch(() => res(im));
          im.onerror = rej; im.src = src;
        });
        this.cache.set(src, p);
      }
      await this.cache.get(src);
    } catch (_) { /* set src anyway */ }
    if (src2x) img.srcset = `${src} 1x, ${src2x} 2x`; else img.removeAttribute('srcset');
    img.src = src; img.hidden = false;
  }
  render() {
    const base = this.cfg.bases[this.state.base];
    if (!base) { if (this.priceEl) this.priceEl.textContent = ''; return; }
    const baseUnit = this.baseUnit(base);
    this.swapLayer(this.baseImg, (baseUnit && baseUnit.img) || base.img);
    this.baseImg.alt = `Bracciale ${base.label}`;
    // sync tendina finitura + foto del tile base con varianti
    if (base.variants && base.variants.length) {
      const sel = this.querySelector(`.pc-base-variant-select[data-base-id="${this.state.base}"]`);
      if (sel) sel.value = String(this.state.baseVariant);
      const tileImg = this.querySelector(`input[name="pc-base"][value="${this.state.base}"]`)?.closest('.pc-base-tile')?.querySelector('img');
      if (tileImg && baseUnit) tileImg.src = this.pieceThumb(base, baseUnit);
    }
    // rail = chosen pieces, each a remove button (direct manipulation)
    const slots = this.assignSlots();
    if (this.rail) {
      this.rail.innerHTML = slots.length
        ? slots.map(([cid, vi], i) => {
            const it = this.cfg.charms[cid]; const v = this.variant(cid, vi);
            if (!it || !v) return '';
            const t = this.esc(it.label + ' — ' + v.label);
            // rail circle wants the TRANSPARENT cutout (img.src), not the cream-tile thumb
            return `<button type="button" class="pc-rail-item" data-remove="${i}" title="Rimuovi ${t}" aria-label="Rimuovi ${t}">`
              + `<img src="${this.pieceImg(it, v).src}" alt="${t}" loading="lazy" decoding="async"><span class="pc-rail-x" aria-hidden="true">×</span></button>`;
          }).join('')
        : '';
    }
    // base radios
    this.querySelectorAll('input[name="pc-base"]').forEach((r) => { r.checked = r.value === this.state.base; });
    // sr-only composition summary (labels escaped)
    this.selectedEl.innerHTML = this.state.charms.length
      ? this.state.charms.map(([cid, vi], i) => {
          const c = this.cfg.charms[cid]; const v = this.variant(cid, vi);
          if (!c || !v) return '';
          return `<li class="pc-chip"><span>${this.esc(c.label)} · ${this.esc(v.label)}</span>`
          + `<button type="button" class="pc-chip-x" data-remove="${i}" aria-label="${this.esc(this.cfg.i18n?.remove || 'Rimuovi')}">×</button></li>`; }).join('')
      : `<li class="pc-selected-empty">Nessun pezzo aggiunto</li>`;
    // charm tiles: aria-pressed + count of that charm; disable all at max
    const full = this.state.charms.length >= this.maxCharms;
    this.querySelectorAll('.pc-charm-tile').forEach((b) => {
      const n = this.state.charms.filter(([c]) => c === b.dataset.charmId).length;
      b.disabled = full;
      b.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
      b.dataset.count = n;
    });
    // at max, lock the variant selectors too (tile add is disabled — keep them consistent)
    this.querySelectorAll('.pc-variant-select').forEach((s) => { s.disabled = full; });
    // per-category badges + max note
    if (this.badges) {
      ['elemento', 'ciondolo'].forEach((cat) => {
        const badge = this.badges[cat]; if (!badge) return;
        const n = this.state.charms.filter(([c]) => this.cfg.charms[c]?.category === cat).length;
        badge.textContent = n; badge.hidden = n === 0;
      });
    }
    if (this.maxNotes) this.maxNotes.forEach((n) => { n.hidden = !full; });
    // price + count, with tick on change
    const total = this.totalCents();
    this.priceEl.textContent = this.formatMoney(total);
    if (this.prevTotal !== null && this.prevTotal !== total) {
      this.priceEl.classList.remove('pc-tick'); void this.priceEl.offsetWidth; this.priceEl.classList.add('pc-tick');
    }
    this.prevTotal = total;
    if (this.countEl) {
      const n = this.state.charms.length;
      this.countEl.textContent = `${base.label} · ${n} ${n === 1 ? 'pezzo' : 'pezzi'}`;
    }
    this.renderBreakdown();
    try { history.replaceState(null, '', `?${this.stateToQuery()}`); } catch (_) { /* noop */ }
    this.dispatchEvent(new CustomEvent('configurator:change',
      { bubbles: true, detail: { base: this.state.base, charms: this.state.charms, totalCents: total } }));
  }
  formatMoney(cents) {
    // Shopify money_format uses tokens like {{amount}}, {{amount_with_comma_separator}},
    // {{amount_no_decimals}}, {{amount_no_decimals_with_comma_separator}} — handle them all.
    const noDecimals = /no_decimals/.test(this.moneyFormat);
    const amount = (cents / 100).toLocaleString('it-IT',
      noDecimals ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return this.moneyFormat.replace(/\{\{\s*amount[a-z_]*\s*\}\}/g, amount);
  }
  esc(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // ---- events ----
  wireEvents() {
    this.addEventListener('change', (e) => {
      if (e.target.name === 'pc-base') { this.state.base = e.target.value; this.state.baseVariant = 0; this.render(); return; }
      if (e.target.classList.contains('pc-base-variant-select')) {
        const bid = e.target.dataset.baseId;
        if (bid && this.cfg.bases[bid]) this.state.base = bid;
        this.state.baseVariant = parseInt(e.target.value, 10) || 0;
        const radio = this.querySelector(`input[name="pc-base"][value="${this.state.base}"]`);
        if (radio) radio.checked = true;
        this.render();
        return;
      }
      // variant <select> on a tile: reflect chosen price in the tile (no add)
      if (e.target.classList.contains('pc-variant-select')) {
        const opt = e.target.selectedOptions[0];
        const charmEl = e.target.closest('.pc-charm');
        const priceEl = charmEl?.querySelector('[data-pc-tile-price]');
        if (opt && priceEl) priceEl.textContent = this.formatMoney(parseInt(opt.dataset.cents, 10));
        // swap foto del tile sulla variante scelta
        const cid = e.target.dataset.charmId;
        const c = this.cfg.charms[cid];
        const v = this.variant(cid, parseInt(e.target.value, 10) || 0);
        const tileImg = charmEl?.querySelector('.pc-charm-tile img');
        if (c && tileImg) tileImg.src = this.pieceThumb(c, v);
      }
    });
    this.addEventListener('click', (e) => {
      const ex = e.target.closest('.pc-example');
      if (ex) { this.loadPreset(ex.dataset.exampleId); return; }
      const tile = e.target.closest('.pc-charm-tile');
      if (tile && !tile.disabled && this.state.charms.length < this.maxCharms) {
        const sel = tile.closest('.pc-charm')?.querySelector('.pc-variant-select');
        const vi = sel ? parseInt(sel.value, 10) || 0 : 0;
        this.state.charms.push([tile.dataset.charmId, vi]); this.render(); return;
      }
      const rm = e.target.closest('[data-remove]');
      if (rm) { this.state.charms.splice(+rm.dataset.remove, 1); this.render(); return; }
      if (e.target.closest('[data-goto-examples]')) { this.querySelector('.pc-examples-strip')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    });
    const addBtn = this.querySelector('[data-pc-add]');
    if (addBtn) addBtn.addEventListener('click', () => this.addToCart());
  }
  hydrateFromUrl() {
    const s = this.queryToState(location.search);
    if (s.base) this.state.base = s.base;
    this.state.charms = s.charms;
  }
  loadPreset(id) {
    const ex = (this.cfg.examples || []).find((e) => e.id === id);
    if (!ex || !ex.preset) return;
    if (this.cfg.bases[ex.preset.base]) this.state.base = ex.preset.base;
    this.state.charms = (ex.preset.items || [])
      .filter(([cid]) => this.cfg.charms[cid] && this.cfg.charms[cid].variants && this.cfg.charms[cid].variants.length)
      .map(([cid, vi]) => { const n = this.cfg.charms[cid].variants.length; return [cid, (vi >= 0 && vi < n) ? vi : 0]; })
      .slice(0, this.maxCharms);
    this.render();
    this.stage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  preloadEager() {
    Object.values(this.cfg.bases).forEach((b) => b.img && b.img.src && this.swapLayer(new Image(), b.img));
  }

  // ---- price breakdown ----
  renderBreakdown() {
    if (!this.breakdownEl) return;
    const base = this.cfg.bases[this.state.base];
    if (!base) { this.breakdownEl.innerHTML = ''; return; }
    const bu = this.baseUnit(base);
    const baseLabel = base.variants ? `${base.label} · ${bu.label}` : base.label;
    const rows = [`<li><span>${this.esc(baseLabel)}</span><span>${this.formatMoney(bu.priceCents)}</span></li>`];
    for (const [cid, vi] of this.state.charms.slice(0, this.maxCharms)) {
      const c = this.cfg.charms[cid], v = this.variant(cid, vi);
      if (!c || !v) continue;
      rows.push(`<li><span>${this.esc(c.label)} <em>${this.esc(v.label)}</em></span><span>${this.formatMoney(v.priceCents)}</span></li>`);
    }
    rows.push(`<li class="pc-breakdown-total"><span>Totale</span><span>${this.formatMoney(this.totalCents())}</span></li>`);
    this.breakdownEl.innerHTML = `<ul>${rows.join('')}</ul>`;
    if (this.breakdownBtn) {
      const open = this.breakdownBtn.getAttribute('aria-expanded') === 'true';
      this.breakdownBtn.textContent = open ? 'Nascondi dettaglio ▴' : 'Dettaglio prezzo ▾';
    }
  }
  toggleBreakdown() {
    const open = this.breakdownBtn.getAttribute('aria-expanded') === 'true';
    this.breakdownBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
    this.breakdownEl.hidden = open;
    this.renderBreakdown();
  }

  // ---- share ----
  async share() {
    const url = location.href;
    try {
      if (navigator.share) { await navigator.share({ title: document.title, url }); return; }
      await navigator.clipboard.writeText(url);
      this.shareBtn.textContent = 'Link copiato ✓';
      setTimeout(() => { this.shareBtn.textContent = 'Condividi'; }, 2000);
    } catch (_) { /* dismissed / blocked */ }
  }

  // ---- cart (atomic bundle add, app-free) ----
  buildItems() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const bundleId = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    const base = this.cfg.bases[this.state.base];
    if (!base) return { root, items: [] };
    const baseUnit = this.baseUnit(base);
    const items = [{
      id: baseUnit.variantId, quantity: 1,
      properties: { _bundle_id: bundleId, _bundle_role: 'base', _config_url: location.href,
        'Finitura': base.variants ? `${base.label} · ${baseUnit.label}` : base.label }
    }];
    for (const [cid, vi] of this.state.charms.slice(0, this.maxCharms)) {
      const c = this.cfg.charms[cid], v = this.variant(cid, vi);
      if (!c || !v) continue;
      items.push({ id: v.variantId, quantity: 1,
        properties: { _bundle_id: bundleId, _bundle_role: 'charm', _part_of: this.cfg.i18n?.bundleName || 'Città del Vento',
          'Pezzo': `${c.label} · ${v.label}` } });
    }
    return { root, items };
  }
  async addToCart() {
    const btn = this.querySelector('[data-pc-add]');
    const { root, items } = this.buildItems();
    if (!items.length || items.some((i) => !i.id)) { this.showMsg('Configurazione non disponibile.', true); return; }
    if (btn) btn.disabled = true;
    try {
      const res = await fetch(`${root}cart/add.js`, { method: 'POST',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        this.showMsg(err.description || (this.cfg.i18n && this.cfg.i18n.soldOut) || 'Esaurito', true); return;
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      location.href = `${root}cart`;
    } catch (_) { this.showMsg('Errore di rete, riprova.', true); }
    finally { if (btn) btn.disabled = false; }
  }
  showMsg(text, isError) {
    const el = this.querySelector('[data-pc-msg]');
    if (!el) return;
    el.textContent = text; el.hidden = false; el.classList.toggle('pc-msg--error', !!isError);
  }
}
customElements.define('palpiti-configurator', PalpitiConfigurator);
